<template>
  <div
    ref="section"
    v-auto-scroll="{
      enabled: () => isMultiSelectHolding,
      orientation: 'horizontal',
      speed: 4,
      padding: 10,
      onScroll: (speed) => {
        $emit('scroll', { pixelY: 0, pixelX: speed })
        return false
      },
    }"
  >
    <div
      v-for="({ left }, index) in groupByDividers"
      :key="'group-by-divider-' + index"
      class="grid-view__group-by-divider"
      :style="{ left: left + 'px' }"
    ></div>
    <HorizontalResize
      v-for="({ groupBy, left }, index) in groupByDividers"
      :key="'group-by-width-' + index"
      class="grid-view__head-group-width-handle"
      :style="{ left: left + 'px' }"
      :width="groupBy.width"
      :min="GRID_VIEW_MIN_FIELD_WIDTH"
      @move="moveGroupWidth(groupBy, view, $event)"
      @update="updateGroupWidth(groupBy, view, database, readOnly, $event)"
    ></HorizontalResize>
    <div
      class="grid-view__inner"
      :class="{ 'grid-view__inner--group-bands': groupBandsMode }"
      :style="{ 'min-width': width + 'px' }"
    >
      <GridViewHead
        :database="database"
        :table="table"
        :view="view"
        :all-fields-in-table="allFieldsInTable"
        :visible-fields="visibleFields"
        :include-row-details="includeRowDetails"
        :include-add-field="includeAddField"
        :include-grid-view-identifier-dropdown="
          includeGridViewIdentifierDropdown
        "
        :include-group-by="includeGroupBy"
        :read-only="readOnly"
        :store-prefix="storePrefix"
        @field-created="$emit('field-created', $event)"
        @refresh="$emit('refresh', $event)"
        @dragging="handleFieldDragging($event)"
      ></GridViewHead>
      <div
        ref="body"
        v-auto-scroll="{
          enabled: () => isMultiSelectHolding,
          speed: 4,
          padding: 10,
          onScroll: (speed) => {
            $emit('scroll', { pixelY: speed, pixelX: 0 })
            return false
          },
        }"
        class="grid-view__body"
      >
        <div class="grid-view__body-inner">
          <GridViewPlaceholder
            :visible-fields="visibleFields"
            :view="view"
            :include-row-details="includeRowDetails"
            :include-group-by="includeGroupBy"
            :store-prefix="storePrefix"
          ></GridViewPlaceholder>
          <GridViewGroups
            v-if="includeGroupBy && activeGroupBys.length > 0"
            :all-fields-in-table="allFieldsInTable"
            :group-by-value-sets="groupByValueSets"
            :store-prefix="storePrefix"
            :hide-cells="groupBandsMode"
            :can-add-row="canCreateRows"
            @add-row-in-group="$emit('add-row-in-group', $event)"
          ></GridViewGroups>
          <GridViewRows
            v-if="includeRowDetails || visibleFields.length > 0"
            ref="rows"
            :view="view"
            :database="database"
            :rendered-fields="fieldsToRender"
            :visible-fields="visibleFields"
            :all-visible-fields="allVisibleFields"
            :all-fields-in-table="allFieldsInTable"
            :workspace-id="database.workspace.id"
            :decorations-by-place="decorationsByPlace"
            :left-offset="fieldsLeftOffset"
            :include-row-details="includeRowDetails"
            :include-group-by="includeGroupBy"
            :rows-at-end-of-groups="rowsAtEndOfGroups"
            :group-start-rows="groupStartRows"
            :group-end-rows="groupBandExtras.endRows"
            :empty-groups-before="groupBandExtras.before"
            :trailing-empty-groups="groupBandExtras.trailing"
            :trailing-after-row-id="groupBandExtras.trailingAfterRowId"
            :can-add-row="canCreateRows"
            :read-only="readOnly"
            :can-drag="
              $hasPermission(
                'database.table.update_row',
                table,
                database.workspace.id
              )
            "
            :store-prefix="storePrefix"
            @update="$emit('update', $event)"
            @paste="$emit('paste', $event)"
            @edit="$emit('edit', $event)"
            @cell-mousedown-left="$emit('cell-mousedown-left', $event)"
            @cell-mouseover="$emit('cell-mouseover', $event)"
            @cell-mouseup-left="$emit('cell-mouseup-left', $event)"
            @cell-shift-click="$emit('cell-shift-click', $event)"
            @cell-selected="$emit('cell-selected', $event)"
            @selected="$emit('selected', $event)"
            @unselected="$emit('unselected', $event)"
            @select="$emit('select', $event)"
            @unselect="$emit('unselect', $event)"
            @select-next="$emit('select-next', $event)"
            @add-row-after="$emit('add-row-after', $event)"
            @edit-modal="$emit('edit-modal', $event)"
            @refresh-row="$emit('refresh-row', $event)"
            @row-dragging="$emit('row-dragging', $event)"
            @row-hover="$emit('row-hover', $event)"
            @row-context="$emit('row-context', $event)"
            @add-row-in-group="$emit('add-row-in-group', $event)"
            @field-created="$emit('field-created', $event)"
            @field-updated="$emit('field-updated', $event)"
            @field-deleted="$emit('field-deleted')"
            @refresh="$emit('refresh', $event)"
          ></GridViewRows>
          <GridViewRowAdd
            v-if="
              !readOnly &&
              (!table.data_sync || table.data_sync.two_way_sync) &&
              (includeRowDetails || visibleFields.length > 0) &&
              ($hasPermission(
                'database.table.create_row',
                table,
                database.workspace.id
              ) ||
                $hasPermission(
                  'database.table.view.create_row',
                  view,
                  database.workspace.id
                ))
            "
            :visible-fields="visibleFields"
            :include-row-details="includeRowDetails"
            :store-prefix="storePrefix"
            @add-row="$emit('add-row', $event)"
            @add-rows="$emit('add-rows', $event)"
          ></GridViewRowAdd>
          <div v-else class="grid-view__row-placeholder"></div>
        </div>
      </div>
      <div class="grid-view__foot">
        <div v-if="includeRowDetails" class="grid-view__foot-info">
          {{ $t('gridView.rowCount', { count }) }}
        </div>
        <div
          v-for="field in visibleFields"
          :key="field.id"
          :style="{ width: getFieldWidth(field) + 'px' }"
        >
          <GridViewFieldFooter
            :database="database"
            :field="field"
            :view="view"
            :store-prefix="storePrefix"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import debounce from 'lodash/debounce'

import GridViewHead from '@baserow/modules/database/components/view/grid/GridViewHead'
import GridViewPlaceholder from '@baserow/modules/database/components/view/grid/GridViewPlaceholder'
import GridViewGroups from '@baserow/modules/database/components/view/grid/GridViewGroups'
import GridViewRows from '@baserow/modules/database/components/view/grid/GridViewRows'
import GridViewRowAdd from '@baserow/modules/database/components/view/grid/GridViewRowAdd'
import gridViewHelpers from '@baserow/modules/database/mixins/gridViewHelpers'
import GridViewFieldFooter from '@baserow/modules/database/components/view/grid/GridViewFieldFooter'
import HorizontalResize from '@baserow/modules/core/components/HorizontalResize'
import { fieldValuesAreEqualInObjects } from '@baserow/modules/database/utils/groupBy'

export default {
  name: 'GridViewSection',
  components: {
    HorizontalResize,
    GridViewHead,
    GridViewPlaceholder,
    GridViewGroups,
    GridViewRows,
    GridViewRowAdd,
    GridViewFieldFooter,
  },
  mixins: [gridViewHelpers],
  props: {
    visibleFields: {
      type: Array,
      required: true,
    },
    allVisibleFields: {
      type: Array,
      required: true,
    },
    allFieldsInTable: {
      type: Array,
      required: true,
    },
    decorationsByPlace: {
      type: Object,
      required: true,
    },
    database: {
      type: Object,
      required: true,
    },
    table: {
      type: Object,
      required: true,
    },
    view: {
      type: Object,
      required: true,
    },
    includeRowDetails: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    includeGroupBy: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    includeAddField: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    includeGridViewIdentifierDropdown: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    canOrderFields: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    readOnly: {
      type: Boolean,
      required: true,
    },
  },
  emits: [
    'update',
    'paste',
    'edit',
    'cell-mousedown-left',
    'cell-mouseover',
    'cell-mouseup-left',
    'cell-shift-click',
    'cell-selected',
    'selected',
    'unselected',
    'select',
    'unselect',
    'select-next',
    'add-row',
    'add-rows',
    'add-row-after',
    'add-row-in-group',
    'edit-modal',
    'refresh-row',
    'row-dragging',
    'row-hover',
    'row-context',
    'scroll',
    'field-created',
    'field-updated',
    'field-deleted',
    'field-dragging',
    'refresh',
  ],
  data() {
    return {
      // Render the first 20 fields by default so that there's at least some data when
      // the page is server side rendered.
      fieldsToRender: this.visibleFields.slice(0, 20),
      // Indicates the offset
      fieldsLeftOffset: 0,
      resizeObserver: null,
      horizontalScrollEvent: null,
    }
  },
  computed: {
    /**
     * Calculates the total width of the whole section based on the fields and the
     * given options.
     */
    width() {
      let width = Object.values(this.visibleFields).reduce(
        (value, field) => this.getFieldWidth(field) + value,
        0
      )

      if (this.includeRowDetails) {
        width += this.gridViewRowDetailsWidth
      }

      // The add button has a width of 100 and we reserve 100 at the right side.
      if (this.includeAddField && !this.groupBandsMode) {
        width += 100 + 100
      } else if (this.includeAddField && this.groupBandsMode) {
        // Origem Flow: no modo banda o cabeçalho global está oculto, mas o
        // conteúdo ainda passa da soma dos campos: o link "Adicionar" do
        // cabeçalho de cada grupo (~76px) e a coluna de comentários das
        // linhas (~41px). Sem essa reserva o min-width fica menor que o
        // conteúdo real e a rolagem horizontal em telas estreitas nunca
        // alcança o final (o "Adicionar coluna" e os comentários somem).
        width += 100
      }

      return width
    },
    groupByDividers() {
      if (!this.includeGroupBy) {
        return []
      }

      let last = 0
      const dividers = this.activeGroupBys
        .filter((groupBy, index) => index < this.activeGroupBys.length - 1)
        .map((groupBy) => {
          last += groupBy.width
          return { groupBy, left: last }
        })

      return dividers
    },
    /**
     * Computes an object that can be used by the `GridViewGroups` and `GridViewRows`
     * components to correctly visualize the groups. Even though both components need
     * different data, we're computing it in the same function because having only one
     * loop is more efficient.
     *
     * groupBySets:
     *
     * Every entry in the array represents a group, and contains a list of spans, which
     * are essentially a row span of the rows in that group.
     *
     * [
     *   {
     *     "groupBy": object,
     *     "groupSpans": [
     *       {
     *         "rowSpan": 10,
     *         "value": any,
     *       },
     *       ...
     *     ]
     *   },
     *   ...
     * ]
     *
     * rowsAtEndOfGroups:
     *
     * Indicates whether the row is the start or end of the last group. This is needed
     * to add a visual divider
     *
     * [1, 2]
     *
     */
    groupBySetsAndRowsAtEndOfGroups() {
      const groupBys = this.activeGroupBys
      const metadata = this.groupByMetadata
      const rows = this.allRows
      const rowsAtEndOfGroups = new Set()

      const groupBySets = groupBys.map((groupBy, groupByIndex) => {
        const groupSpans = []
        let lastGroup = null

        rows.forEach((row, index) => {
          const previousRow = rows[index - 1]
          const nextRow = rows[index + 1]

          /**
           * Helper function that checks whether the value is the same for both rows in
           * this group, but also the previous ones. This is needed because we need to
           * start a new group if the previous value doesn't match.
           */
          const checkIfInSameGroup = (row1, row2) => {
            if (row1 === undefined || row2 === undefined) {
              return false
            }
            return groupBys.slice(0, groupByIndex + 1).every((groupBy) => {
              const groupByField = this.allFieldsInTable.find(
                (f) => f.id === groupBy.field
              )
              if (!groupByField) {
                return false
              }
              const groupByFieldType = this.$registry.get(
                'field',
                groupByField.type
              )
              return groupByFieldType.isEqual(
                groupByField,
                row1[`field_${groupBy.field}`],
                row2[`field_${groupBy.field}`]
              )
            })
          }

          if (!checkIfInSameGroup(previousRow, row)) {
            // The group by metadata is a dict where the key is equal to the group by,
            // and the value an array containing the count for each unique value
            // combination. Below we're looking through the entries to find the
            // matching count for the row values.
            const count =
              (metadata[`field_${groupBy.field}`] || []).find((entry) => {
                const groupByFields = groupBys
                  .slice(0, groupByIndex + 1)
                  .map((groupBy) =>
                    this.allFieldsInTable.find((f) => f.id === groupBy.field)
                  )
                  .filter(Boolean)
                return fieldValuesAreEqualInObjects(
                  groupByFields,
                  this.$registry,
                  entry,
                  row,
                  true
                )
              })?.count || -1

            // If the start of a group, then create a new span object in the last.
            lastGroup = {
              rowSpan: 1,
              value: row[`field_${groupBy.field}`],
              count,
            }
          } else {
            // If the value hasn't changed, it means that this row falls within the
            // already started group, to we have to increase the row span.
            lastGroup.rowSpan += 1
          }

          if (!checkIfInSameGroup(row, nextRow)) {
            // If the group ends, it must be added to the array.
            groupSpans.push(lastGroup)
            lastGroup = null

            // If we're at the last group, we want to store whether the row is last so
            // that we can visually show divider. This is only needed for the last group
            // because that's where the divider must match the one with the group.
            if (groupByIndex === groupBys.length - 1) {
              rowsAtEndOfGroups.add(row.id)
            }
          }
        })

        return { groupBy, groupSpans }
      })

      return { groupBySets, rowsAtEndOfGroups }
    },
    groupByValueSets() {
      return this.groupBySetsAndRowsAtEndOfGroups.groupBySets
    },
    rowsAtEndOfGroups() {
      return this.groupBySetsAndRowsAtEndOfGroups.rowsAtEndOfGroups
    },
    /**
     * Origem Flow: modo "banda de grupo" estilo ClickUp — ativo quando há
     * exatamente um group by. As pills saem da gutter lateral e viram uma
     * linha horizontal de largura total renderizada junto das linhas.
     */
    groupBandsMode() {
      return this.activeGroupBys.length === 1
    },
    /**
     * Mapa rowId -> { groupBy, field, value, count } das linhas que iniciam
     * um grupo (nível único). A primeira linha do buffer só conta como
     * início de grupo real quando o buffer começa no topo da tabela, para a
     * banda não aparecer no meio de um grupo ao rolar tabelas grandes.
     */
    groupStartRows() {
      if (!this.groupBandsMode) {
        return {}
      }
      const groupBy = this.activeGroupBys[0]
      const field = this.allFieldsInTable.find((f) => f.id === groupBy.field)
      if (!field) {
        return {}
      }
      const fieldType = this.$registry.get('field', field.type)
      const bufferStartIndex =
        this.$store.getters[this.storePrefix + 'view/grid/getBufferStartIndex']
      const starts = {}
      this.allRows.forEach((row, index) => {
        const previousRow = this.allRows[index - 1]
        const sameGroup =
          previousRow !== undefined &&
          fieldType.isEqual(
            field,
            previousRow[`field_${groupBy.field}`],
            row[`field_${groupBy.field}`]
          )
        if (sameGroup || (index === 0 && bufferStartIndex > 0)) {
          return
        }
        const count =
          (this.groupByMetadata[`field_${groupBy.field}`] || []).find(
            (entry) =>
              fieldValuesAreEqualInObjects(
                [field],
                this.$registry,
                entry,
                row,
                true
              )
          )?.count ?? -1
        starts[row.id] = {
          groupBy,
          field,
          value: row[`field_${groupBy.field}`],
          count,
        }
      })
      return starts
    },
    /**
     * Origem Flow: complementos ClickUp da banda de grupo — grupos vazios
     * sempre visíveis e linha "+ Adicionar tarefa" persistente no fim de
     * cada grupo.
     *
     * - `before`: mapa rowId -> [grupo vazio, ...] a renderizar antes da
     *   banda daquela linha (opções do single select sem nenhuma linha,
     *   intercaladas na posição correta da ordenação).
     * - `trailing`: grupos vazios que vêm depois da última linha (ou todos,
     *   quando a tabela está vazia).
     * - `trailingAfterRowId`: id da última linha do buffer, onde os grupos
     *   `trailing` devem ser renderizados.
     * - `endRows`: mapa rowId -> payload do "+ Adicionar tarefa" das linhas
     *   que fecham um grupo com linhas.
     *
     * Só é calculado quando o buffer cobre a tabela inteira — em tabelas
     * muito grandes (buffer parcial) degrada para o comportamento antigo,
     * mesma limitação conhecida da banda de grupo.
     */
    groupBandExtras() {
      const extras = {
        before: {},
        trailing: [],
        trailingAfterRowId: null,
        endRows: {},
      }
      if (!this.groupBandsMode) {
        return extras
      }
      const groupBy = this.activeGroupBys[0]
      const field = this.allFieldsInTable.find((f) => f.id === groupBy.field)
      if (!field) {
        return extras
      }
      const bufferStartIndex =
        this.$store.getters[this.storePrefix + 'view/grid/getBufferStartIndex']
      const rows = this.allRows
      if (bufferStartIndex > 0 || rows.length < this.count) {
        return extras
      }
      const key = `field_${field.id}`
      if (rows.length > 0) {
        extras.trailingAfterRowId = rows[rows.length - 1].id
        rows.forEach((row) => {
          if (this.rowsAtEndOfGroups.has(row.id)) {
            extras.endRows[row.id] = { groupBy, field, value: row[key] }
          }
        })
      }
      if (field.type === 'single_select') {
        // Espelha a ordenação do backend: no tipo padrão as opções ordenam
        // alfabeticamente pelo valor (collation); no tipo 'order', pela
        // posição da opção no campo. DESC inverte; nulos ficam no início em
        // ASC (nulls_first) e no fim em DESC (nulls_last).
        const options = (field.select_options || []).slice()
        if ((groupBy.type || 'default') !== 'order') {
          options.sort((a, b) =>
            (a.value || '').localeCompare(b.value || '', undefined, {
              sensitivity: 'base',
            })
          )
        }
        const desc = (groupBy.order || 'ASC').toUpperCase() === 'DESC'
        if (desc) {
          options.reverse()
        }
        const nullPosition = desc ? Infinity : -1
        const position = new Map(options.map((option, i) => [option.id, i]))
        const present = new Set(
          rows.map((row) => row[key]?.id ?? null).filter((id) => id !== null)
        )
        options.forEach((option, pos) => {
          if (present.has(option.id)) {
            return
          }
          const group = {
            groupBy,
            field,
            value: {
              id: option.id,
              value: option.value,
              color: option.color,
            },
            count: 0,
          }
          // A primeira linha cujo grupo ordena depois desta opção é sempre o
          // início de um grupo — a banda vazia entra logo antes dela.
          const target = rows.find((row) => {
            const value = row[key]
            const rowPos =
              value && position.has(value.id)
                ? position.get(value.id)
                : nullPosition
            return rowPos > pos
          })
          if (target) {
            // Âncora para a criação: a tarefa nova do grupo vazio nasce
            // exatamente onde a banda está, antes desta linha.
            group.beforeRowId = target.id
            if (extras.before[target.id] === undefined) {
              extras.before[target.id] = []
            }
            extras.before[target.id].push(group)
          } else {
            extras.trailing.push(group)
          }
        })
      }
      return extras
    },
    isMultiSelectHolding() {
      return this.$store.getters[
        this.storePrefix + 'view/grid/isMultiSelectHolding'
      ]
    },
    count() {
      return this.$store.getters[this.storePrefix + 'view/grid/getCount']
    },
    allRows() {
      return this.$store.getters[this.storePrefix + 'view/grid/getAllRows']
    },
    groupByMetadata() {
      return this.$store.getters[
        this.storePrefix + 'view/grid/getGroupByMetadata'
      ]
    },
    /**
     * Origem Flow: mesma regra de permissão do GridViewRowAdd, reutilizada
     * pelo "+ adicionar" dos grupos (gutter e banda).
     */
    canCreateRows() {
      return (
        !this.readOnly &&
        (!this.table.data_sync || this.table.data_sync.two_way_sync) &&
        (this.$hasPermission(
          'database.table.create_row',
          this.table,
          this.database.workspace.id
        ) ||
          this.$hasPermission(
            'database.table.view.create_row',
            this.view,
            this.database.workspace.id
          ))
      )
    },
  },
  watch: {
    fieldOptions: {
      deep: true,
      handler() {
        this.updateVisibleFieldsInRow()
      },
    },
    visibleFields: {
      deep: true,
      handler() {
        this.updateVisibleFieldsInRow()
      },
    },
  },
  /*beforeCreate() {
    this.$options.computed = {
      ...(this.$options.computed || {}),
      ...mapGetters({
        isMultiSelectHolding:
          this.$options.propsData.storePrefix +
          'view/grid/isMultiSelectHolding',
        count: this.$options.propsData.storePrefix + 'view/grid/getCount',
        allRows: this.$options.propsData.storePrefix + 'view/grid/getAllRows',
        groupByMetadata:
          this.$options.propsData.storePrefix + 'view/grid/getGroupByMetadata',
      }),
    }
  },*/
  mounted() {
    // When the component first loads, we need to check
    this.updateVisibleFieldsInRow()

    const updateDebounced = debounce(() => {
      this.updateVisibleFieldsInRow()
    }, 50)

    const sectionElement = this.$refs.section

    // When the viewport resizes, we need to check if there are fields that must be
    // rendered.
    this.resizeObserver = new ResizeObserver(() => {
      updateDebounced()
    })
    this.resizeObserver.observe(sectionElement)

    // When the user scrolls horizontally, we need to check if there fields/cells that
    // have moved into the viewport and must be rendered.
    const fireUpdateBuffer = {
      last: Date.now(),
      distance: 0,
    }
    this.horizontalScrollEvent = (event) => {
      // Call the update order debounce function to simulate a stop scrolling event.
      updateDebounced()

      const now = Date.now()
      const { scrollLeft } = event.target

      const distance = Math.abs(scrollLeft - fireUpdateBuffer.distance)
      const timeDelta = now - fireUpdateBuffer.last

      if (timeDelta > 100) {
        const velocity = distance / timeDelta

        fireUpdateBuffer.last = now
        fireUpdateBuffer.distance = scrollLeft

        if (velocity < 2.5) {
          updateDebounced.cancel()
          this.updateVisibleFieldsInRow()
        }
      }
    }
    sectionElement.addEventListener('scroll', this.horizontalScrollEvent)
  },
  beforeUnmount() {
    const sectionElement = this.$refs.section
    if (this.resizeObserver !== null) {
      this.resizeObserver.disconnect()
    }
    if (this.horizontalScrollEvent !== null) {
      sectionElement.removeEventListener('scroll', this.horizontalScrollEvent)
    }
  },
  methods: {
    handleFieldDragging(event) {
      if (!this.canOrderFields || event.field.primary) return
      this.$emit('field-dragging', event)
    },
    /**
     * For performance reasons we only want to render the cells are visible in the
     * viewport. This method makes sure that the right cells/fields are visible. It's
     * for example called when the user scrolls, when the window is resized or when a
     * field changes.
     */
    updateVisibleFieldsInRow() {
      if (!this.$el) return
      const width = this.$el.clientWidth
      const scrollLeft = this.$el.scrollLeft
      // The padding is added to the start and end of the viewport to make sure that
      // cells nearby will always be ready to be displayed.
      const padding = 200
      const viewportStart = scrollLeft - padding
      const viewportEnd = scrollLeft + width + padding
      let leftOffset = null
      let left = 0

      // Create an array containing the fields that are currently visible in the
      // viewport and must be rendered.
      const fieldsToRender = this.visibleFields.filter((field) => {
        const width = this.getFieldWidth(field)
        const right = left + width
        const visible = right >= viewportStart && left <= viewportEnd
        if (visible && leftOffset === null) {
          leftOffset = left
        }
        left = right
        return visible
      })

      if (
        JSON.stringify(this.fieldsToRender) !== JSON.stringify(fieldsToRender)
      ) {
        this.fieldsToRender = fieldsToRender
      }

      if (leftOffset !== this.fieldsLeftOffset) {
        this.fieldsLeftOffset = leftOffset
      }
    },
    getScrollElement() {
      return this.$refs.section
    },
  },
}
</script>
